"use client";

import { c as _c } from "react/compiler-runtime";
import { RiAddLine, RiCalendarLine, RiChat1Line, RiCheckDoubleLine, RiCircleLine, RiFlagLine, RiGroupLine, RiPencilLine, RiUserLine } from "react-icons/ri";
import { calculateRelativeDuration, formatDate, formatStaffName } from "@/lib/format-data";
import { TaskLogAction, TaskLogDetail, TaskPriority, TaskStatus } from "@/types/task";
import { useTaskActivity } from "@/api/provider/tasks/tasks-query";
interface TaskActivityLogProps {
  taskId: string;
}
const STATUS_LABELS: Record<TaskStatus, string> = {
  [TaskStatus.OPEN]: "Open",
  [TaskStatus.IN_PROGRESS]: "In Progress",
  [TaskStatus.COMPLETED]: "Completed",
  [TaskStatus.CANCELLED]: "Cancelled"
};
const STATUS_COLORS: Record<TaskStatus, string> = {
  [TaskStatus.OPEN]: "text-foreground-muted",
  [TaskStatus.IN_PROGRESS]: "text-status-warning-text",
  [TaskStatus.COMPLETED]: "text-status-success-text",
  [TaskStatus.CANCELLED]: "text-foreground-muted"
};
const PRIORITY_LABELS: Record<TaskPriority, string> = {
  [TaskPriority.URGENT]: "Urgent",
  [TaskPriority.HIGH]: "High",
  [TaskPriority.NORMAL]: "Normal",
  [TaskPriority.LOW]: "Low"
};
const PRIORITY_COLORS: Record<TaskPriority, string> = {
  [TaskPriority.URGENT]: "text-status-error-text",
  [TaskPriority.HIGH]: "text-status-warning-text",
  [TaskPriority.NORMAL]: "text-foreground-muted",
  [TaskPriority.LOW]: "text-foreground-muted"
};
export function getActivityIcon(log: TaskLogDetail): {
  icon: React.ComponentType<{
    className?: string;
  }>;
  color: string;
} {
  switch (log.actionType) {
    case TaskLogAction.CREATED:
      return {
        icon: RiAddLine,
        color: "text-foreground-muted"
      };
    case TaskLogAction.STATUS_CHANGED:
      {
        return {
          icon: RiCircleLine,
          color: STATUS_COLORS[log.data.newStatus] ?? "text-foreground-muted"
        };
      }
    case TaskLogAction.PRIORITY_CHANGED:
      {
        return {
          icon: RiFlagLine,
          color: PRIORITY_COLORS[log.data.newPriority] ?? "text-foreground-muted"
        };
      }
    case TaskLogAction.ASSIGNED:
      return {
        icon: RiUserLine,
        color: "text-foreground-muted"
      };
    case TaskLogAction.DUE_DATE_CHANGED:
      return {
        icon: RiCalendarLine,
        color: "text-status-error-text"
      };
    case TaskLogAction.COMPLETED:
      return {
        icon: RiCheckDoubleLine,
        color: "text-status-success-text"
      };
    case TaskLogAction.TITLE_CHANGED:
      return {
        icon: RiPencilLine,
        color: "text-foreground-muted"
      };
    case TaskLogAction.DESCRIPTION_CHANGED:
      return {
        icon: RiPencilLine,
        color: "text-foreground-muted"
      };
    case TaskLogAction.PATIENT_CHANGED:
      return {
        icon: RiUserLine,
        color: "text-foreground-muted"
      };
    case TaskLogAction.START_DATE_CHANGED:
      return {
        icon: RiCalendarLine,
        color: "text-accent-foreground"
      };
    case TaskLogAction.SUBSCRIBERS_CHANGED:
      return {
        icon: RiGroupLine,
        color: "text-foreground-muted"
      };
    case TaskLogAction.COMMENT_ADDED:
      return {
        icon: RiChat1Line,
        color: "text-foreground-muted"
      };
    default:
      return {
        icon: RiCircleLine,
        color: "text-foreground-muted"
      };
  }
}
const B = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="font-medium text-foreground">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export function formatActivityDescription(log: TaskLogDetail): React.ReactNode {
  const actorName = log.actor ? formatStaffName(log.actor) : "Someone";
  switch (log.actionType) {
    case TaskLogAction.CREATED:
      return <>{actorName} created this task</>;
    case TaskLogAction.STATUS_CHANGED:
      {
        const {
          oldStatus,
          newStatus
        } = log.data;
        const newLabel = STATUS_LABELS[newStatus];
        const oldLabel = oldStatus ? STATUS_LABELS[oldStatus] : undefined;
        if (oldLabel) {
          return <>
            {actorName} moved from <B>{oldLabel}</B> to <B>{newLabel}</B>
          </>;
        }
        return <>
          {actorName} set status to <B>{newLabel}</B>
        </>;
      }
    case TaskLogAction.PRIORITY_CHANGED:
      {
        const {
          oldPriority,
          newPriority
        } = log.data;
        const newLabel = PRIORITY_LABELS[newPriority];
        const oldLabel = oldPriority ? PRIORITY_LABELS[oldPriority] : undefined;
        if (oldPriority) {
          const oldIndex = Object.values(TaskPriority).indexOf(oldPriority);
          const newIndex = Object.values(TaskPriority).indexOf(newPriority);
          const verb = newIndex < oldIndex ? "raised" : "lowered";
          return <>
            {actorName} {verb} priority from <B>{oldLabel}</B> to{" "}
            <B>{newLabel}</B>
          </>;
        }
        return <>
          {actorName} set priority to <B>{newLabel}</B>
        </>;
      }
    case TaskLogAction.ASSIGNED:
      {
        const {
          oldAssigneeName,
          newAssigneeName
        } = log.data;
        if (newAssigneeName) {
          if (oldAssigneeName) {
            return <>
              {actorName} re-assigned to <B>{newAssigneeName}</B>
            </>;
          }
          return <>
            {actorName} assigned to <B>{newAssigneeName}</B>
          </>;
        }
        if (oldAssigneeName) {
          return <>
            {actorName} unassigned from <B>{oldAssigneeName}</B>
          </>;
        }
        return <>{actorName} updated assignee</>;
      }
    case TaskLogAction.DUE_DATE_CHANGED:
      {
        const {
          oldDueDate,
          newDueDate
        } = log.data;
        if (oldDueDate && newDueDate) {
          return <>
            {actorName} changed due date from{" "}
            <B>{formatDate(oldDueDate, {
                calendarDay: true
              })}</B> to{" "}
            <B>{formatDate(newDueDate, {
                calendarDay: true
              })}</B>
          </>;
        }
        if (newDueDate) {
          return <>
            {actorName} set due date to{" "}
            <B>{formatDate(newDueDate, {
                calendarDay: true
              })}</B>
          </>;
        }
        return <>{actorName} removed the due date</>;
      }
    case TaskLogAction.TITLE_CHANGED:
      {
        const {
          oldTitle,
          newTitle
        } = log.data;
        return <>
          {actorName} changed title from <B>{oldTitle}</B> to <B>{newTitle}</B>
        </>;
      }
    case TaskLogAction.DESCRIPTION_CHANGED:
      return <>{actorName} updated the description</>;
    case TaskLogAction.PATIENT_CHANGED:
      {
        const {
          oldPatientName,
          newPatientName
        } = log.data;
        if (oldPatientName && newPatientName) {
          return <>
            {actorName} changed patient from <B>{oldPatientName}</B> to{" "}
            <B>{newPatientName}</B>
          </>;
        }
        if (newPatientName) {
          return <>
            {actorName} linked patient <B>{newPatientName}</B>
          </>;
        }
        if (oldPatientName) {
          return <>
            {actorName} unlinked patient <B>{oldPatientName}</B>
          </>;
        }
        return <>{actorName} updated patient</>;
      }
    case TaskLogAction.START_DATE_CHANGED:
      {
        const {
          oldStartDate,
          newStartDate
        } = log.data;
        if (oldStartDate && newStartDate) {
          return <>
            {actorName} changed start date from{" "}
            <B>{formatDate(oldStartDate, {
                calendarDay: true
              })}</B> to{" "}
            <B>{formatDate(newStartDate, {
                calendarDay: true
              })}</B>
          </>;
        }
        if (newStartDate) {
          return <>
            {actorName} set start date to{" "}
            <B>{formatDate(newStartDate, {
                calendarDay: true
              })}</B>
          </>;
        }
        return <>{actorName} removed the start date</>;
      }
    case TaskLogAction.SUBSCRIBERS_CHANGED:
      {
        const {
          addedNames = [],
          removedNames = []
        } = log.data ?? {};
        const parts: string[] = [];
        if (addedNames.length > 0) {
          parts.push(`added ${addedNames.join(", ")}`);
        }
        if (removedNames.length > 0) {
          parts.push(`removed ${removedNames.join(", ")}`);
        }
        return <>
          {actorName} {parts.join(" and ")}
        </>;
      }
    case TaskLogAction.COMPLETED:
      return <>{actorName} completed this task</>;
    case TaskLogAction.COMMENT_ADDED:
      return <>{actorName} added a comment</>;
    default:
      return <>{actorName} made a change</>;
  }
}
export function TaskActivityLog(t0) {
  const $ = _c(8);
  const {
    taskId
  } = t0;
  const {
    data: activities,
    isLoading
  } = useTaskActivity(taskId);
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <h3 className="text-body-strong text-foreground">Activity</h3>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="space-y-3">{t1}<div className="flex items-center justify-center py-4"><div className="h-4 w-4 animate-spin rounded-full border-2 border-foreground-muted border-t-transparent" /></div></div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (!activities || activities.length === 0) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="space-y-3"><h3 className="text-body-strong text-foreground">Activity</h3><p className="text-body text-foreground-secondary">No activity yet</p></div>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="text-body-strong text-foreground">Activity</h3>;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== activities) {
    t2 = activities.map(_temp);
    $[4] = activities;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <div className="space-y-3">{t1}<div className="space-y-2 max-h-64 overflow-y-auto">{t2}</div></div>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp(log) {
  const {
    icon: Icon,
    color
  } = getActivityIcon(log);
  return <div key={log.id} className="flex items-center gap-2 text-body"><Icon className={`size-4 shrink-0 ${color}`} /><span className="text-foreground-secondary truncate">{formatActivityDescription(log)}</span><span className="text-foreground-muted shrink-0">·</span><span className="text-foreground-muted shrink-0 whitespace-nowrap">{calculateRelativeDuration(log.createdDate)}</span></div>;
}